// Signing in from an ordinary browser.
//
// The Activity has its own handshake (`activity.ts`): Discord is already on the
// other side of the iframe, so the SDK asks it for a code directly. In a
// browser there is nobody to ask, so this is the standard OAuth redirect --
// leave for discord.com, come back to /auth with a code, swap it for a token on
// the server.
//
// Both paths end in the same place: a Discord access token that the server can
// turn into an identity by asking Discord. Nothing here believes the browser
// about who anyone is, and neither does the server.
//
// The client secret is not here and cannot be. A public OAuth client in a page
// is the whole reason `/api/token` exists.

import { DISCORD_CLIENT_ID } from './activity.ts';

/**
 * Where Discord sends the browser back to.
 *
 * Its own route, not the site root, for two reasons. It has to be registered
 * with Discord character for character -- a redirect that carries `?show=true`
 * would have to be registered with it -- and a dedicated path means the return
 * is recognisable before anything else has run.
 */
export const AUTH_PATH = '/auth';

/** Where the player lands once they are signed in. Keeps the curtain up. */
export const HOME = '/?show=true';

const TOKEN_KEY = 'riftbound.auth';
const GUEST_KEY = 'riftbound.guest';
const STATE_KEY = 'riftbound.auth.state';

/** A token and when it stops being one. */
export interface SavedAuth {
  token: string;
  /** Epoch ms. Discord's tokens last a week; after that this asks again. */
  expires: number;
}

export interface LoginResult {
  token: string | null;
  /** Why it failed, when it did. Shown on the sign-in screen, not swallowed. */
  error?: string;
}

/**
 * Storage that cannot throw.
 *
 * Safari in private browsing, and any browser with site data switched off,
 * throws from `localStorage` rather than returning null -- so a player with
 * cookies disabled would crash on the way in instead of simply not being
 * remembered. Not being remembered is the correct degradation.
 */
function read(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: Storage | undefined, key: string, value: string | null): void {
  try {
    if (value === null) store?.removeItem(key);
    else store?.setItem(key, value);
  } catch {
    /* not remembered, still playable */
  }
}

const local = (): Storage | undefined => globalThis.localStorage;
const session = (): Storage | undefined => globalThis.sessionStorage;

/** The saved sign-in, if there is one and it has not expired. */
export function savedAuth(): SavedAuth | null {
  const raw = read(local(), TOKEN_KEY);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as Partial<SavedAuth>;
    if (typeof saved.token !== 'string' || typeof saved.expires !== 'number') return null;
    // A minute of slack, so a token that expires mid-boot is treated as gone
    // rather than used for one request and refused.
    if (saved.expires <= Date.now() + 60_000) return null;
    return { token: saved.token, expires: saved.expires };
  } catch {
    return null;
  }
}

export function saveAuth(token: string, expiresInSeconds: number): void {
  // A week is Discord's default. Clamped so a malformed or absurd value cannot
  // park a dead token in storage for years.
  const ttl = Number.isFinite(expiresInSeconds)
    ? Math.max(60, Math.min(expiresInSeconds, 30 * 24 * 3600))
    : 7 * 24 * 3600;
  write(local(), TOKEN_KEY, JSON.stringify({ token, expires: Date.now() + ttl * 1000 }));
  write(local(), GUEST_KEY, null);
}

/** Forgets the sign-in. The next launch asks again. */
export function signOut(): void {
  write(local(), TOKEN_KEY, null);
  write(local(), GUEST_KEY, null);
}

/**
 * Whether the player has already said they want to play as a guest.
 *
 * Remembered for the same reason the token is: being asked to choose on every
 * single launch is the thing that makes a web page feel unlike an app.
 */
export function guestChosen(): boolean {
  return read(local(), GUEST_KEY) === '1';
}

export function chooseGuest(): void {
  write(local(), GUEST_KEY, '1');
}

/** The redirect Discord must have registered, built from wherever we are. */
export function redirectUri(): string {
  return `${globalThis.location.origin}${AUTH_PATH}`;
}

/** Whether this page load is Discord sending the browser back. */
export function isAuthCallback(): boolean {
  const loc = globalThis.location;
  if (!loc || loc.pathname !== AUTH_PATH) return false;
  const params = new URLSearchParams(loc.search);
  return params.has('code') || params.has('error');
}

/**
 * Leaves for Discord's consent screen. Does not return.
 *
 * `state` is a nonce kept in session storage and checked on the way back. It is
 * what makes the callback unforgeable: without it, anyone could hand this
 * browser a `/auth?code=...` link and have it exchange a code they chose.
 */
export function beginLogin(): void {
  const state = crypto.randomUUID();
  write(session(), STATE_KEY, state);

  const url = new URL('https://discord.com/oauth2/authorize');
  url.searchParams.set('client_id', DISCORD_CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri());
  // `identify` alone, because that is all this uses -- a name and an avatar.
  // Every extra scope is another thing for a player to refuse.
  url.searchParams.set('scope', 'identify');
  url.searchParams.set('state', state);
  globalThis.location.assign(url.toString());
}

/**
 * Finishes the redirect: checks the nonce, swaps the code for a token, and
 * puts the address bar back to something worth bookmarking.
 *
 * Always cleans the URL, including when it failed -- a `code` left in history
 * is a credential in the browser's history, and a reload would try to spend a
 * code that has already been used.
 */
export async function completeLogin(): Promise<LoginResult> {
  const params = new URLSearchParams(globalThis.location.search);
  const expected = read(session(), STATE_KEY);
  write(session(), STATE_KEY, null);

  const done = (result: LoginResult): LoginResult => {
    try {
      globalThis.history.replaceState(null, '', HOME);
    } catch {
      /* a history that will not rewrite is cosmetic, not fatal */
    }
    return result;
  };

  const denied = params.get('error');
  if (denied) {
    // "access_denied" is someone pressing Cancel, which is not an error worth
    // showing them their own decision about.
    return done(denied === 'access_denied'
      ? { token: null }
      : { token: null, error: params.get('error_description') ?? denied });
  }

  const code = params.get('code');
  const state = params.get('state');
  if (!code) return done({ token: null, error: 'Discord sent no code.' });
  if (!expected || state !== expected) {
    return done({ token: null, error: 'Sign-in did not match the request that started it.' });
  }

  try {
    const res = await fetch('/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri() }),
    });
    if (!res.ok) throw new Error(`token exchange failed: ${res.status}`);
    const payload = await res.json() as { access_token?: string; expires_in?: number };
    if (!payload.access_token) throw new Error('token exchange returned no access token');
    saveAuth(payload.access_token, payload.expires_in ?? 7 * 24 * 3600);
    return done({ token: payload.access_token });
  } catch (err) {
    return done({ token: null, error: err instanceof Error ? err.message : String(err) });
  }
}
